import { HttpContextToken, HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from "@angular/common/http";
import { Inject, Injectable } from "@angular/core";
import { MatSnackBar } from "@angular/material/snack-bar";
import { catchError, finalize, Observable, shareReplay, switchMap, throwError } from "rxjs";
import { LoginResult } from "../models/login-result.model";
import { AccountService } from "../services/account.service";

const REFRESH_TOKEN_PATH = "/auth/users/refresh-token";

// AT-1224: marca a request já repetida com o token renovado, para (a) nunca tentar
// refresh de novo nela (evita loop) e (b) se ELA MESMA tomar 401 de novo, tratar como
// sessão irrecuperável em vez de tentar renovar mais uma vez.
const IS_RETRY_AFTER_REFRESH = new HttpContextToken<boolean>(() => false);

@Injectable()
export class HttpInterceptorMiddleware implements HttpInterceptor {

    // AT-1224: single-flight — o refresh token é rotacionado a cada chamada (mesmo motivo
    // já documentado no create-http-client.ts do lado React); múltiplas requisições que
    // tomam 401 ao mesmo tempo compartilham esta ÚNICA tentativa de refresh, em vez de cada
    // uma disparar a sua — o que mataria a sessão, já que reaproveitar o refresh token
    // antigo depois que outra chamada já o rotacionou falha.
    private refreshInProgress$: Observable<LoginResult> | null = null;

    constructor(
        @Inject('API_GATEWAY_URL') private apiGatewayUrl: any,
        @Inject('APP_WORKSPACE_URL') private appWorkspaceUrl: any,
        private _snackbar: MatSnackBar,
        private readonly _accountService: AccountService,
    ) {
    }

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        if (this._accountService.isLoggedIn()) {
            // Validar se já existe um header de autorização para a requisição atual
            // Caso já exista não deve sobrescrever pois pode ser uma requisição externa. Ex.: CNPJá
            if (!req.headers.get("Authorization")) {
                const token = this._accountService.getToken();

                if (!!token)
                    req = req.clone({ headers: req.headers.set('Authorization', `Bearer ${token}`) });

                const account = this._accountService.selectedAccount;

                if (!!account)
                    req = req.clone({
                        headers: req.headers.set('X-Account-Id', `${account.id}`)
                            .set('X-Account-Domain', account.domain)
                    });
            }
        }

        return next.handle(req).pipe(
            catchError((err: unknown) => this._handleError(req, next, err)),
        );
    }

    // AT-1224: handler de erro compartilhado — usado tanto pela request original quanto,
    // recursivamente, pela request repetida após o refresh (via `_handleUnauthorized`).
    // Isso garante que um erro na request REPETIDA (ex.: um 403/404 legítimo do próprio
    // recurso, depois de um refresh bem-sucedido) recebe o tratamento normal (snackbar
    // correto) em vez de ser confundido com uma falha do refresh em si.
    private _handleError(
        req: HttpRequest<any>,
        next: HttpHandler,
        err: unknown,
    ): Observable<HttpEvent<any>> {
        if (!(err instanceof HttpErrorResponse)) {
            this._snackbar.open('Erro interno do Angular.', 'Fechar');
            return throwError(() => err);
        }

        if (!navigator.onLine) {
            this._snackbar.open('Não foi possível se comunicar com o servidor.');
            return throwError(() => err);
        }

        let message: string | null = null;

        if (err.error)
            message = `${err.error.title}${(!!err.error.detail ? ': ' + err.error.detail : '')}`;

        switch (err.status) {
            case 400:
                this._snackbar.open(message || 'Má requisição.');
                return throwError(() => err);
            case 401: {
                // AT-1224: renovação silenciosa antes de qualquer coisa visível — só pra
                // requests do gateway, e nunca para o próprio endpoint de refresh (senão
                // reentraria no próprio fluxo de 401 tratado aqui).
                const isGatewayRequest = req.url.includes(this.apiGatewayUrl) && !req.url.includes(REFRESH_TOKEN_PATH);
                const alreadyRetried = req.context.get(IS_RETRY_AFTER_REFRESH);

                if (isGatewayRequest && !alreadyRetried)
                    return this._handleUnauthorized(req, next, err);

                if (isGatewayRequest && alreadyRetried) {
                    // Token recém-renovado e MESMO ASSIM 401: sessão irrecuperável, desiste.
                    this._redirectToLogin();
                    return throwError(() => err);
                }

                this._snackbar.open(message || 'Não autorizado.');
                return throwError(() => err);
            }
            case 403:
                this._snackbar.open(message || 'Acesso negado.');
                return throwError(() => err);
            case 404:
                this._snackbar.open(message || 'Não encontrado.');
                return throwError(() => err);
            case 500:
                this._snackbar.open(message || 'Erro interno do servidor.');
                return throwError(() => err);
            default:
                this._snackbar.open('Não foi possível se conectar com o servidor.');
                return throwError(() => err);
        }
    }

    // AT-1224: o usuário não deve perceber que o token expirou — tenta renovar por baixo
    // dos panos e repete a MESMA requisição que tomou 401 com o token novo. Só cai num
    // fluxo visível (redirect ao login) se o refresh também falhar por a sessão estar de
    // fato morta (refresh token inválido/expirado); falha transitória (rede/5xx) não
    // desloga — propaga o 401 original, a sessão pode continuar viva no servidor.
    private _handleUnauthorized(
        req: HttpRequest<any>,
        next: HttpHandler,
        originalError: HttpErrorResponse,
    ): Observable<HttpEvent<any>> {
        if (!this.refreshInProgress$) {
            this.refreshInProgress$ = this._accountService.refreshToken().pipe(
                shareReplay(1),
                finalize(() => { this.refreshInProgress$ = null; }),
            );
        }

        return this.refreshInProgress$.pipe(
            catchError((refreshErr: unknown) => {
                const status = refreshErr instanceof HttpErrorResponse ? refreshErr.status : undefined;
                const isSessionDead = status !== undefined && status >= 400 && status < 500;

                if (isSessionDead)
                    this._redirectToLogin();

                // Sempre propaga o 401 ORIGINAL (não o erro do refresh) — mantém o
                // contrato já existente pro chamador, tanto no caso transitório quanto no
                // caso de sessão morta (o redirect já está em andamento nesse segundo caso).
                return throwError(() => originalError);
            }),
            switchMap((result) => {
                const retriedReq = req.clone({
                    headers: req.headers.set('Authorization', `Bearer ${result.token}`),
                    context: req.context.set(IS_RETRY_AFTER_REFRESH, true),
                });

                return next.handle(retriedReq).pipe(
                    catchError((retryErr: unknown) => this._handleError(retriedReq, next, retryErr)),
                );
            }),
        );
    }

    // AT-1224 (RF04): guarda a URL completa (origem + rota + query) da aba atual — este
    // interceptor é compartilhado por vários apps Angular em origens diferentes
    // (Workspace, Docs, Reports, Pointer), então só path+query não bastaria para retomar
    // a tela certa quando o 401 acontece fora do Workspace (o browser resolveria um path
    // relativo contra a origem do Workspace, não a origem de onde veio). Corrige também
    // o bug em que o returnUrl era omitido quando o 401 acontecia dentro do próprio
    // Workspace (`isWks`).
    private _redirectToLogin(): void {
        const returnUrl = `${window.location.origin}${window.location.pathname}${window.location.search}`;
        const returnUrlParams = `?returnUrl=${encodeURIComponent(returnUrl)}`;

        window.location.href = `${this.appWorkspaceUrl}/login/refresh-token${returnUrlParams}`;
    }
}
